import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Rating,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  LocalShipping,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi } from '../../services/simpleApi';

interface Supplier {
  id: string;
  name: string;
  contact: string;
  email: string;
  phone: string;
  category: string;
  status: 'active' | 'inactive' | 'at_risk';
  rating: number;
  onTimeDelivery: number;
  qualityScore: number;
  totalOrders: number;
  location: string;
}

const SuppliersPage: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  // State management using simpleApi
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await simpleApi.suppliers.list();
      // Map API response to frontend format
      const mappedSuppliers = response.items.map((s: any) => ({
        id: s.id,
        name: s.name,
        contact: s.contact_name || '',
        email: s.email || '',
        phone: s.phone || '',
        category: 'General',
        status: s.status || 'active',
        rating: s.reliability_score * 5 || 4,
        onTimeDelivery: s.reliability_score * 100 || 85,
        qualityScore: s.reliability_score * 100 || 90,
        totalOrders: 0,
        location: s.city ? `${s.city}, ${s.country || ''}` : s.country || '',
      }));
      setSuppliers(mappedSuppliers);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchItems();
  }, []);

  const clearSuccess = () => setSuccess(null);

  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    phone: '',
    category: '',
    location: '',
  });

  const handleOpenDialog = (supplier?: Supplier) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        contact: supplier.contact,
        email: supplier.email,
        phone: supplier.phone,
        category: supplier.category,
        location: supplier.location,
      });
    } else {
      setEditingSupplier(null);
      setFormData({
        name: '',
        contact: '',
        email: '',
        phone: '',
        category: '',
        location: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplier(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const apiData = {
        name: formData.name,
        contact_name: formData.contact,
        email: formData.email,
        phone: formData.phone,
        city: formData.location.split(',')[0]?.trim() || '',
        country: formData.location.split(',')[1]?.trim() || '',
      };
      
      if (editingSupplier) {
        await simpleApi.suppliers.update(editingSupplier.id, apiData);
        setSuccess('Supplier updated successfully');
      } else {
        await simpleApi.suppliers.create(apiData);
        setSuccess('Supplier created successfully');
      }
      await fetchItems();
      handleCloseDialog();
    } catch (error: any) {
      setError(error.message || 'Error saving supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await simpleApi.suppliers.delete(id);
        setSuccess('Supplier deleted successfully');
        await fetchItems();
      } catch (error: any) {
        setError(error.message || 'Failed to delete supplier');
      }
    }
  };

  const filteredSuppliers = suppliers.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'at_risk':
        return 'warning';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle />;
      case 'at_risk':
        return <Warning />;
      default:
        return null;
    }
  };

  const getPerformanceTrend = (score: number) => {
    if (score >= 90)
      return { icon: <TrendingUp />, color: theme.palette.success.main };
    if (score >= 75)
      return { icon: <TrendingUp />, color: theme.palette.warning.main };
    return { icon: <TrendingDown />, color: theme.palette.error.main };
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Suppliers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your supplier relationships and performance
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Supplier
        </Button>
      </Box>

      {error && <ErrorAlert error={error} onRetry={fetchItems} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                {suppliers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Suppliers
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: theme.palette.success.main }}
              >
                {suppliers.filter((s) => s.status === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Suppliers
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: theme.palette.warning.main }}
              >
                {suppliers.filter((s) => s.status === 'at_risk').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                At Risk
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: theme.palette.secondary.main }}
              >
                {suppliers.length > 0
                  ? (
                      suppliers.reduce((sum, s) => sum + s.onTimeDelivery, 0) /
                      suppliers.length
                    ).toFixed(0)
                  : 0}
                %
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg On-Time Delivery
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Card>

      {/* Suppliers Table */}
      <Card>
        {loading ? (
          <LoadingState message="Loading suppliers..." />
        ) : filteredSuppliers.length === 0 ? (
          <EmptyState
            title="No suppliers found"
            description="Get started by adding your first supplier"
            actionLabel="Add Supplier"
            onAction={() => handleOpenDialog()}
            icon={<LocalShipping sx={{ fontSize: 64, color: 'text.disabled' }} />}
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>On-Time %</TableCell>
                  <TableCell>Quality</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.map((supplier) => {
                  const performanceTrend = getPerformanceTrend(
                    supplier.onTimeDelivery
                  );
                  return (
                    <TableRow key={supplier.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalShipping color="action" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {supplier.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {supplier.location}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{supplier.contact}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {supplier.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{supplier.category}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(supplier.status) || undefined}
                          label={supplier.status.replace('_', ' ').toUpperCase()}
                          size="small"
                          color={getStatusColor(supplier.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Rating
                          value={supplier.rating}
                          precision={0.5}
                          size="small"
                          readOnly
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 60 }}>
                            <LinearProgress
                              variant="determinate"
                              value={supplier.onTimeDelivery}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(performanceTrend.color, 0.1),
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: performanceTrend.color,
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {supplier.onTimeDelivery}%
                          </Typography>
                          <Box sx={{ color: performanceTrend.color }}>
                            {performanceTrend.icon}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {supplier.qualityScore}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{supplier.totalOrders}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(supplier)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(supplier.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contact}
                onChange={(e) =>
                  setFormData({ ...formData, contact: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingSupplier ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessSnackbar
        open={!!success}
        message={success || ''}
        onClose={clearSuccess}
      />
    </Box>
  );
};

export default SuppliersPage;
